"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Wrench, BrainCircuit, Code as Code2, Scan, Bot, Cpu, Target, ChevronRight, ChevronDown, Info, Globe, Search, FlaskConical, Presentation, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
interface StepItem {
  step: number;
  code: string;
  icon: React.ElementType;
  title: {
    vi: string;
    en: string;
  };
}
interface PhilosophyStage {
  phase: string;
  code: string;
  icon: React.ElementType;
  title: {
    vi: string;
    en: string;
  };
  desc: {
    vi: string;
    en: string;
  };
}
const PHILOSOPHY_STAGES: PhilosophyStage[] = [{
  phase: "01",
  code: "EXPLORE",
  icon: Search,
  title: {
    vi: "KHÁM PHÁ",
    en: "EXPLORE"
  },
  desc: {
    vi: "Tìm hiểu nguyên lý STEM & đặt vấn đề kỹ thuật",
    en: "Discover STEM concepts & engineering problems"
  }
}, {
  phase: "02",
  code: "BUILD",
  icon: Wrench,
  title: {
    vi: "LẮP RÁP",
    en: "BUILD"
  },
  desc: {
    vi: "Thiết kế & hoàn thiện cấu trúc mô hình robot",
    en: "Design & build physical robot structure"
  }
}, {
  phase: "03",
  code: "PROGRAM",
  icon: Code2,
  title: {
    vi: "LẬP TRÌNH",
    en: "PROGRAM"
  },
  desc: {
    vi: "Xây dựng thuật toán & viết mã điều khiển",
    en: "Develop algorithms & write control code"
  }
}, {
  phase: "04",
  code: "TEST",
  icon: FlaskConical,
  title: {
    vi: "THỬ NGHIỆM",
    en: "TEST"
  },
  desc: {
    vi: "Vận hành, phát hiện lỗi & tinh chỉnh tối ưu",
    en: "Run, debug & optimize performance"
  }
}, {
  phase: "05",
  code: "PRESENT",
  icon: Presentation,
  title: {
    vi: "TRÌNH BÀY",
    en: "PRESENT"
  },
  desc: {
    vi: "Báo cáo dự án & bảo vệ giải pháp sáng tạo",
    en: "Present project & defend engineering solution"
  }
}];
const STEPS: StepItem[] = [{
  step: 1,
  code: "BUILD",
  icon: Wrench,
  title: {
    vi: "Xây dựng và cơ khí",
    en: "Construction & mechanics"
  }
}, {
  step: 2,
  code: "THINK",
  icon: BrainCircuit,
  title: {
    vi: "Tư duy thuật toán và trình tự",
    en: "Algorithmic & sequential thinking"
  }
}, {
  step: 3,
  code: "PROGRAM",
  icon: Code2,
  title: {
    vi: "Lập trình robot",
    en: "Robot programming"
  }
}, {
  step: 4,
  code: "SENSE",
  icon: Scan,
  title: {
    vi: "Cảm nhận môi trường bằng cảm biến",
    en: "Sensing the environment"
  }
}, {
  step: 5,
  code: "MOVE",
  icon: Bot,
  title: {
    vi: "Điều khiển robot di động",
    en: "Mobile robot control"
  }
}, {
  step: 6,
  code: "AUTONOMOUS",
  icon: Cpu,
  title: {
    vi: "Robot tự hành",
    en: "Autonomous robots"
  }
}, {
  step: 7,
  code: "MISSION",
  icon: Target,
  title: {
    vi: "Giải quyết nhiệm vụ và chiến lược",
    en: "Mission-solving & strategy"
  }
}];
export default function PathwayPhilosophyProgression() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const syncLang = () => {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "vi" || saved === "en") {
        setLang(saved);
      }
    };
    syncLang();
    window.addEventListener("storage", syncLang);
    window.addEventListener("roboto-lang-change", syncLang);
    return () => {
      window.removeEventListener("storage", syncLang);
      window.removeEventListener("roboto-lang-change", syncLang);
    };
  }, []);
  const toggleLanguage = (newLang: "vi" | "en") => {
    setLang(newLang);
    localStorage.setItem("roboto_lang", newLang);
    window.dispatchEvent(new Event("roboto-lang-change"));
  };
  return <section id="pathwayphilosophyprogression" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 md:py-24 text-foreground">
      {/* Decorative Technical Grid Background Pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.06]" aria-hidden="true" style={{
      backgroundImage: `linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)`,
      backgroundSize: "32px 32px"
    }} />

      {/* Decorative Glow Blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 size-96 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 size-96 rounded-full bg-secondary/15 blur-3xl" aria-hidden="true" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Language Switcher Bar */}
        <div className="mb-6 flex justify-end">
          <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1 shadow-2xs">
            <span className="sr-only">Select Language</span>
            <Globe className="ml-2 size-4 text-muted-foreground" aria-hidden="true" />
            <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => toggleLanguage("vi")} className="h-7 rounded-full px-3 text-xs font-semibold">
              VI
            </Button>
            <Button variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => toggleLanguage("en")} className="h-7 rounded-full px-3 text-xs font-semibold">
              EN
            </Button>
          </div>
        </div>

        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <motion.h2 initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            {lang === "vi" ? "MỘT LỘ TRÌNH – NHIỀU GIAI ĐOẠN PHÁT TRIỂN" : "ONE PATHWAY – MULTIPLE DEVELOPMENTAL STAGES"}
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {lang === "vi" ? "Lộ trình Robotics của ROBOTO được thiết kế theo từng bước phát triển năng lực, từ việc xây dựng cấu trúc và hình thành tư duy kỹ thuật, đến lập trình robot, sử dụng cảm biến, điều khiển robot di động, xây dựng hệ thống tự hành và cuối cùng là giải quyết các nhiệm vụ robot có tính chiến lược." : "ROBOTO's Robotics pathway is structured around progressive competency stages — from physical construction and engineering thinking, through robot programming, sensors, mobile robot control, autonomous systems, and finally strategic mission-solving."}
          </motion.p>
        </div>

        {/* VISUAL PROGRESSION INFOGRAPHIC: Learning Philosophy Stages */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-12 rounded-2xl border border-border/80 bg-[#0B132B] p-6 text-white shadow-xl sm:p-8 lg:p-10">
          {/* Infographic Header Bar */}
          <div className="flex flex-col gap-3 border-b border-white/10 pb-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                <Sparkles className="size-4" aria-hidden="true" />
              </span>
              <div>
                <span className="text-[10px] font-mono font-bold tracking-widest text-secondary uppercase">
                  ROBOTO METHODOLOGY CYCLE
                </span>
                <h3 className="text-lg font-bold text-white sm:text-xl">
                  {lang === "vi" ? "Sơ Đồ Tiến Trình Triết Lý Học Tập 5 Bước" : "5-Step Learning Philosophy Progression Diagram"}
                </h3>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs text-white/80">
              <span className="size-2 rounded-full bg-secondary animate-pulse" />
              <span className="font-mono text-[11px] font-medium tracking-wide">
                EXPLORE → BUILD → PROGRAM → TEST → PRESENT
              </span>
            </div>
          </div>

          {/* Stepped Horizontal Diagram (Desktop Grid, Mobile Stack) */}
          <div className="relative mt-8">
            {/* Connecting Spec Rail Line behind cards on Desktop */}
            <div className="hidden lg:absolute lg:top-1/2 lg:left-8 lg:right-8 lg:z-0 lg:-translate-y-1/2 lg:block lg:h-0.5 lg:bg-gradient-to-r lg:from-secondary/30 lg:via-accent/40 lg:to-secondary/30" aria-hidden="true" />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5 lg:gap-3">
              {PHILOSOPHY_STAGES.map((stage, idx) => {
              const Icon = stage.icon;
              const isLast = idx === PHILOSOPHY_STAGES.length - 1;
              return <motion.div key={stage.code} data-index={idx} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.35,
                delay: idx * 0.08
              }} className="group relative z-10 flex flex-col">
                    {/* Stepped Node Card */}
                    <div className="relative flex flex-1 flex-col justify-between rounded-xl border border-white/15 bg-[#111C3A] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-secondary hover:bg-[#162348] hover:shadow-lg">
                      {/* Top Spec Notch Line */}
                      <div className="mb-3 flex items-center justify-between">
                        <span className="rounded-md bg-secondary/20 px-2 py-0.5 font-mono text-[11px] font-bold text-secondary">
                          PHASE {stage.phase}
                        </span>
                        <span className="text-[10px] font-mono font-bold tracking-wider text-white/50 uppercase">
                          {stage.code}
                        </span>
                      </div>

                      {/* Stage Icon Circle */}
                      <div className="my-2 flex size-12 items-center justify-center rounded-xl bg-white/10 text-secondary transition-colors duration-200 group-hover:bg-secondary group-hover:text-secondary-foreground">
                        <Icon className="size-6 shrink-0" aria-hidden="true" />
                      </div>

                      {/* Stage Title & Subtitle */}
                      <div className="mt-2">
                        <h4 className="text-sm font-bold tracking-tight text-white sm:text-base">
                          {stage.title[lang]}
                        </h4>
                        <p className="mt-1 text-xs leading-relaxed text-white/70">
                          {stage.desc[lang]}
                        </p>
                      </div>

                      {/* Active Indicator Bar */}
                      <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-white/10">
                        <div className="h-full w-full origin-left scale-x-0 bg-secondary transition-transform duration-300 group-hover:scale-x-100" />
                      </div>
                    </div>

                    {/* Step Connective Arrow */}
                    {!isLast && <>
                        {/* Desktop Connector Chevron */}
                        <div className="hidden lg:absolute lg:top-1/2 lg:-right-3.5 lg:z-20 lg:-translate-y-1/2 lg:flex lg:size-7 lg:items-center lg:justify-center lg:rounded-full lg:border lg:border-white/20 lg:bg-[#0B132B] lg:text-secondary lg:shadow-xs" aria-hidden="true">
                          <ChevronRight className="size-4" />
                        </div>

                        {/* Mobile Connector Chevron */}
                        <div className="my-1 flex justify-center lg:hidden" aria-hidden="true">
                          <div className="flex size-6 items-center justify-center rounded-full border border-white/20 bg-[#111C3A] text-secondary">
                            <ChevronDown className="size-3.5" />
                          </div>
                        </div>
                      </>}
                  </motion.div>;
            })}
            </div>
          </div>

          {/* Infographic Footer Summary Rail */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4 text-xs text-white/60">
            <div className="flex items-center gap-2">
              <ArrowRight className="size-3.5 text-secondary" aria-hidden="true" />
              <span>
                {lang === "vi" ? "Chu trình được lặp lại & nâng cấp ở từng cấp độ khóa học" : "The cycle is repeated & leveled up in each course stage"}
              </span>
            </div>
            <div className="font-mono text-[11px] text-white/40">
              LAB BLUEPRINT // PHILOSOPHY PROGRESSION
            </div>
          </div>
        </motion.div>

        {/* Sub-label for Detailed Developmental Pathway */}
        <div className="mt-14 mb-6 text-center">
          <span className="inline-block rounded-md bg-muted px-3 py-1 font-mono text-xs font-bold tracking-wider text-muted-foreground uppercase">
            {lang === "vi" ? "7 BƯỚC PHÁT TRIỂN NĂNG LỰC TOÀN DIỆN" : "7 COMPREHENSIVE COMPETENCY STAGES"}
          </span>
        </div>

        {/* Horizontal Progression Flow (Desktop Horizontal, Mobile Vertical) */}
        <div className="mt-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-7 lg:gap-2">
            {STEPS.map((item, idx) => {
            const Icon = item.icon;
            const isLast = idx === STEPS.length - 1;
            return <motion.div key={item.step} data-index={idx} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.35,
              delay: idx * 0.06
            }} className="group relative flex flex-col items-center">
                  {/* Card Container */}
                  <div className="relative flex w-full flex-col items-center justify-between rounded-xl border border-border bg-card p-4 text-center shadow-xs transition-all duration-300 group-hover:-translate-y-1 group-hover:border-primary/50 group-hover:shadow-md lg:min-h-[220px]">
                    {/* Step Number Badge & Code */}
                    <div className="flex items-center justify-between w-full">
                      <span className="flex size-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        0{item.step}
                      </span>
                      <span className="text-[10px] font-mono font-bold tracking-wider text-muted-foreground uppercase">
                        {item.code}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className="my-3 flex size-12 items-center justify-center rounded-lg bg-muted text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="size-6 shrink-0" aria-hidden="true" />
                    </div>

                    {/* Title */}
                    <h3 className="text-xs font-semibold leading-snug text-foreground sm:text-sm">
                      {item.title[lang]}
                    </h3>
                  </div>

                  {/* Connective Arrow (Desktop Right Arrow, Mobile Down Arrow) */}
                  {!isLast && <>
                      {/* Desktop Arrow */}
                      <div className="hidden lg:absolute lg:top-1/2 lg:-right-3 lg:z-20 lg:-translate-y-1/2 lg:flex lg:items-center lg:justify-center" aria-hidden="true">
                        <div className="flex size-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-2xs">
                          <ChevronRight className="size-3.5" />
                        </div>
                      </div>

                      {/* Mobile Arrow */}
                      <div className="my-1.5 flex justify-center lg:hidden" aria-hidden="true">
                        <div className="flex size-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-2xs">
                          <ChevronDown className="size-3.5" />
                        </div>
                      </div>
                    </>}
                </motion.div>;
          })}
          </div>
        </div>

        {/* Bottom Competency Scope Note */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.4
      }} className="mt-12 flex items-start gap-3 rounded-lg border border-border/80 bg-muted/50 p-4 text-xs text-muted-foreground sm:text-sm">
          <Info className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
          <p className="leading-relaxed">
            {lang === "vi" ? "Đây là tiến trình năng lực trải dài xuyên suốt toàn bộ lộ trình D1–HP6, không phải kỹ năng xuất hiện ngay từ khóa D1." : "This is a competency progression spanning the entire D1–HP6 pathway, not something present from day one in D1."}
          </p>
        </motion.div>
      </div>
    </section>;
}